import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GetTableDto } from 'src/app/layout/shell/shell.model';

export interface FileUploadEvent {
  event: File[];
  isDrop: boolean;
}

@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss'],
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilePreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() signedUrl: string = '';
  @Input() mimeType: string = '';
  @Input() rawFileData: GetTableDto = {};
  @Input() showDownloadIcon: boolean = false;
  @Input() isNewDataspace: boolean = true;

  safeUrl: SafeResourceUrl = '';
  fileExtension: string = '';
  iconUrl: string = '';
  isImage = false;
  isPDF = false;

  @ViewChild('imageViewport') imageViewport?: ElementRef<HTMLDivElement>;
  zoom = 1;
  minZoom = 0.25;
  maxZoom = 5;
  translateX = 0;
  translateY = 0;
  isPanning = false;
  lastPointerX = 0;
  lastPointerY = 0;
  imgNaturalWidth = 0;
  imgNaturalHeight = 0;

  valid_MIME_types: string[] = [
    "application/pdf",
    "text/csv",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.spreadsheet",
    "application/vnd.oasis.opendocument.text",
    "application/vnd.apple.numbers",
    "application/vnd.apple.pages",
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    "text/plain",
    "text/markdown",
    "audio/mpeg",
    "audio/wav",
    "audio/ogg",
    "application/vnd.ms-outlook",
    "application/zip",
    "application/x-zip-comprzipessed",
    "application/x-rar-compressed",
    "application/gzip",
    "application/x-7z-compressed",
    "application/json",
  ];

  valid_extensions: string[] = [
    ".pdf",
    ".csv",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ods",
    ".odt",
    ".numbers",
    ".pages",
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".webp",
    ".txt",
    ".md",
    ".markdown",
    ".mp3",
    ".wav",
    ".ogg",
    ".msg",
    ".zip",
    ".rar",
    ".gz",
    ".7z",
    ".json",
  ];

  valid_files = `${this.valid_MIME_types.join(",")},${this.valid_extensions.join(",")}`;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.processUrl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['signedUrl'] || changes['mimeType'] || changes['isNewDataspace']) {
      this.resetState();
      this.processUrl();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.resetState();
  }

  private resetState() {
    this.isImage = false;
    this.isPDF = false;
    this.fileExtension = '';
    this.iconUrl = '';

    this.safeUrl = '';

    this.zoom = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isPanning = false;
    this.lastPointerX = 0;
    this.lastPointerY = 0;
    this.imgNaturalWidth = 0;
    this.imgNaturalHeight = 0;

    this.cdr.markForCheck();
  }

  private processUrl() {
    if (this.signedUrl) {
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.signedUrl);
      this.detectFileType();
    }
  }

  private detectFileType() {
    const mimeType = this.mimeType.toLowerCase();
    const url = this.signedUrl.toLowerCase();

    switch (mimeType) {
      case 'application/pdf':
        this.fileExtension = '.pdf';
        this.isPDF = true;
        break;
      case 'text/csv':
        this.fileExtension = '.csv';
        break;
      case 'application/msword':
        this.fileExtension = '.doc';
        break;
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        this.fileExtension = '.docx';
        break;
      case 'application/vnd.ms-excel':
        this.fileExtension = '.xls';
        break;
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        this.fileExtension = '.xlsx';
        break;
      case 'application/vnd.oasis.opendocument.spreadsheet':
        this.fileExtension = '.ods';
        break;
      case 'application/vnd.oasis.opendocument.text':
        this.fileExtension = '.odt';
        break;
      case 'application/vnd.apple.numbers':
        this.fileExtension = '.numbers';
        break;
      case 'application/vnd.apple.pages':
        this.fileExtension = '.pages';
        break;
      case 'image/png':
        this.fileExtension = '.png';
        this.isImage = true;
        break;
      case 'image/jpeg':
        this.fileExtension = url.endsWith('.jpeg') ? '.jpeg' : '.jpg';
        this.isImage = true;
        break;
      case 'image/gif':
        this.fileExtension = '.gif';
        this.isImage = true;
        break;
      case 'image/webp':
        this.fileExtension = '.webp';
        this.isImage = true;
        break;
      case 'text/plain':
        this.fileExtension = '.txt';
        break;
      case 'text/markdown':
        this.fileExtension = url.endsWith('.markdown') ? '.markdown' : '.md';
        break;
      case 'audio/mpeg':
        this.fileExtension = '.mp3';
        break;
      case 'audio/wav':
        this.fileExtension = '.wav';
        break;
      case 'audio/ogg':
        this.fileExtension = '.ogg';
        break;
      case 'application/vnd.ms-outlook':
        this.fileExtension = '.msg';
        break;
      case 'application/zip':
        this.fileExtension = '.zip';
        break;
      case 'application/x-zip-comprzipessed':
        this.fileExtension = '.zip';
        break;
      case 'application/x-rar-compressed':
        this.fileExtension = '.rar';
        break;
      case 'application/gzip':
        this.fileExtension = '.gz';
        break;
      case 'application/x-7z-compressed':
        this.fileExtension = '.7z';
        break;
      case 'application/json':
        this.fileExtension = '.json';
        break;
    }

    if (this.fileExtension) {
      const ext = this.fileExtension.startsWith('.') ? this.fileExtension.substring(1) : this.fileExtension;
      let category = 'text';
      let displayExt = ext.toUpperCase();

      if (['7z', 'zip', 'rar', 'gz'].includes(ext)) {
        category = 'archive';
      } else if (['csv', 'xls', 'xlsx', 'ods', 'numbers'].includes(ext)) {
        category = 'tables';
      } else if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
        category = 'images';
        if (ext === 'jpg' || ext === 'jpeg') displayExt = 'JPEG';
      } else if (['mp3', 'wav', 'ogg'].includes(ext)) {
        category = 'audio';
      } else if (ext === 'json') {
        category = 'data';
      } else if (ext === 'md' || ext === 'markdown') {
        displayExt = 'MD';
      }

      const iconName = `talonic-${category}-${displayExt}`;
      this.iconUrl = `assets/img/icons/file-types-icons/${iconName}.svg`;
    }
  }

  get transform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoom})`;
  }

  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    this.imgNaturalWidth = img.naturalWidth;
    this.imgNaturalHeight = img.naturalHeight;
    this.zoom = 1;
    this.centerImage();
    this.cdr.markForCheck();
  }

  zoomIn() {
    this.setZoom(this.zoom * 1.2);
  }

  zoomOut() {
    this.setZoom(this.zoom / 1.2);
  }

  resetView() {
    this.zoom = 1;
    this.centerImage();
    this.cdr.markForCheck();
  }

  startPan(event: MouseEvent | TouchEvent) {
    const point = this.getPoint(event);
    this.isPanning = true;
    this.lastPointerX = point.x;
    this.lastPointerY = point.y;
  }

  onPanMove(event: MouseEvent | TouchEvent) {
    if (!this.isPanning) return;
    const point = this.getPoint(event);
    const dx = point.x - this.lastPointerX;
    const dy = point.y - this.lastPointerY;
    this.translateX += dx;
    this.translateY += dy;
    this.lastPointerX = point.x;
    this.lastPointerY = point.y;
    if ((event as any).cancelable) {
      (event as any).preventDefault();
    }
    this.cdr.markForCheck();
  }

  endPan() {
    this.isPanning = false;
  }

  onWheel(event: WheelEvent) {
    if (event.cancelable) {
      event.preventDefault();
    }
    const direction = event.deltaY > 0 ? -1 : 1;
    const factor = direction > 0 ? 1.1 : 0.9;
    this.setZoom(this.zoom * factor);
  }

  private setZoom(newZoom: number) {
    const clamped = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));
    this.zoom = clamped;
    this.centerImage();
    this.cdr.markForCheck();
  }

  private getPoint(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if ((event as TouchEvent).touches && (event as TouchEvent).touches.length) {
      const t = (event as TouchEvent).touches[0];
      return { x: t.clientX, y: t.clientY };
    }
    const m = event as MouseEvent;
    return { x: m.clientX, y: m.clientY };
  }

  private centerImage() {
    const container = this.imageViewport?.nativeElement;
    if (!container || !this.imgNaturalWidth || !this.imgNaturalHeight) {
      return;
    }
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const imgW = this.imgNaturalWidth * this.zoom;
    const imgH = this.imgNaturalHeight * this.zoom;
    const offsetX = Math.max(0, (cw - imgW) / 2);
    const offsetY = Math.max(0, (ch - imgH) / 2);
    this.translateX = offsetX;
    this.translateY = offsetY;
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isImage) this.centerImage();
  }

}
